import axios from 'axios';
import API_BASE_URL from './config/api';

const API_ROOT = `${String(API_BASE_URL || '').replace(/\/+$/, '')}/escala/multiprofissional`;

const createRequestId = () => {
	if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}

	return `escala-multiprofissional-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const authHeaders = (token) => ({
	headers: {
		...(token ? { Authorization: `Token ${token}` } : {}),
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'X-Request-ID': createRequestId(),
	},
});

const compactParams = (params = {}) =>
	Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && String(value) !== '',
		),
	);

const normalizeEstabelecimento = (value) => {
	if (value && typeof value === 'object') {
		return String(value.cnes || value.id || value.codigo || value.value || '').trim();
	}
	return String(value || '').trim();
};

const unwrapList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.results)) return payload.results;
	return [];
};

const buildError = (error, fallbackMessage) => {
	const data = error?.response?.data;
	const detail = data?.detalhe || data?.detail || data?.mensagem || data?.message || fallbackMessage;
	return {
		error: {
			status: error?.response?.status,
			code: data?.codigo || data?.code || '',
			title: data?.titulo || data?.title || '',
			detail,
			message: typeof detail === 'string' ? detail : fallbackMessage,
			issues: Array.isArray(data?.issues) ? data.issues : Array.isArray(data?.erros) ? data.erros : [],
			data: data || null,
		},
	};
};

const request = async (method, path, { token, params, body, headers } = {}) => {
	try {
		const response = await axios({
			method,
			url: `${API_ROOT}/${String(path || '').replace(/^\//, '')}`,
			params: compactParams(params),
			data: body,
			...authHeaders(token),
			headers: { ...authHeaders(token).headers, ...(headers || {}) },
		});
		return response.data;
	} catch (error) {
		return buildError(error, 'Não foi possível concluir a operação da escala multiprofissional.');
	}
};

export const requestFiltrosBuscaEscalaMultiprofissional = ({
	token,
	estabelecimento,
	somenteComItens = false,
} = {}) =>
	request('get', 'escalas-mensais/filtros-busca/', {
		token,
		params: {
			estabelecimento: normalizeEstabelecimento(estabelecimento),
			somente_com_itens: somenteComItens || undefined,
		},
	});

export const requestCatalogosEscalaSelectsMultiprofissional = ({ token, estabelecimento } = {}) =>
	request('get', 'catalogos-escala-selects/', {
		token,
		params: { estabelecimento: normalizeEstabelecimento(estabelecimento) },
	});

export const requestBuscarGradeEscalaMultiprofissional = ({
	token,
	estabelecimento,
	setor,
	identificador,
	regime,
	mes,
	ano,
	categoria,
	nomeProfissional,
	profissionalId,
	tipoItem,
} = {}) =>
	request('get', 'escalas-mensais/buscar-grade/', {
		token,
		params: {
			estabelecimento: normalizeEstabelecimento(estabelecimento),
			setor,
			identificador,
			regime,
			mes,
			ano,
			categoria,
			nome_profissional: nomeProfissional,
			profissional_id: profissionalId,
			tipo_item: tipoItem,
		},
	});

export const requestAbrirAmbienteEscalaMultiprofissional = ({
	token,
	estabelecimento,
	setor,
	mesReferencia,
	modo = 'resumo',
	vinculoProfissionalId,
} = {}) =>
	request('get', 'escalas-mensais/ambiente-preenchimento/', {
		token,
		params: {
			estabelecimento: normalizeEstabelecimento(estabelecimento),
			setor,
			mes_referencia: mesReferencia,
			modo,
			vinculo_profissional_id: vinculoProfissionalId,
			incluir_cards: modo === 'resumo' ? true : undefined,
			incluir_profissionais_sem_cards: modo === 'detalhado' ? true : undefined,
			_refresh: Date.now(),
		},
	});

export const requestOpcoesAmbientePreenchimentoMultiprofissional = ({
	token,
	contexto,
} = {}) =>
	request('get', 'escalas-mensais/ambiente-preenchimento/opcoes/', {
		token,
		params: {
			estabelecimento: normalizeEstabelecimento(contexto?.estabelecimento),
			setor: contexto?.setor,
			mes_referencia: contexto?.mes_referencia,
		},
	});

export const requestPreverAlocacaoOrigemMultiprofissional = ({ token, body } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/alocacao-origem/prever/', {
		token,
		body,
	});

export const requestAplicarAlocacaoOrigemMultiprofissional = ({ token, body, previewEtag, idempotencyKey } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/alocacao-origem/aplicar/', {
		token,
		body,
		headers: {
			...(previewEtag ? { 'If-Match': String(previewEtag).startsWith('"') ? String(previewEtag) : `"${previewEtag}"` } : {}),
			...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
		},
	});

export const requestVinculosEscalaMultiprofissional = async ({ token } = {}) => {
	const result = await request('get', 'vinculos-escala/', { token });
	if (result?.error) return result;
	return unwrapList(result);
};

export const requestListarVinculosProfissionaisMultiprofissional = async ({
	token,
	estabelecimento,
	search,
	pagina,
	tamanhoPagina,
} = {}) => {
	const result = await request('get', 'vinculos-profissionais-estabelecimento/', {
		token,
		params: {
			estabelecimento: normalizeEstabelecimento(estabelecimento),
			search,
			pagina,
			tamanho_pagina: tamanhoPagina,
		},
	});

	if (result?.error) return result;
	return { ...result, results: unwrapList(result) };
};

export const requestCadastrarVinculoProfissionalMultiprofissional = ({ token, body } = {}) =>
	request('post', 'vinculos-profissionais-estabelecimento/', { token, body });

export const requestDetalharVinculoMultiprofissional = ({ token, id }) =>
	request('get', `vinculos-profissionais-estabelecimento/${id}/`, { token });

export const requestContextosOrigemMultiprofissional = ({ token, estabelecimento, mesReferencia, vinculoProfissionalId }) =>
	request('get', 'escalas-mensais/ambiente-preenchimento/alocacao-origem/', {
		token, params: { estabelecimento, mes_referencia: mesReferencia, vinculo_profissional_id: vinculoProfissionalId, _refresh: Date.now() },
	});

export const requestDetalharLinhaMultiprofissional = ({ token, estabelecimento, setor, mesReferencia, regimeVinculoId }) =>
	request('get', 'escalas-mensais/ambiente-preenchimento/linha/', {
		token, params: { estabelecimento, setor, mes_referencia: mesReferencia, regime_vinculo_id: regimeVinculoId },
	});

export const requestAtualizarVinculoProfissionalMultiprofissional = ({ token, id, body } = {}) =>
	request('patch', `vinculos-profissionais-estabelecimento/${id}/`, { token, body });

export const requestBuscarProfissionalEscalaMultiprofissional = async ({
	token,
	cpf,
	termo,
	estabelecimento,
	vinculo,
	dataReferencia,
} = {}) => {
	const result = await request('get', 'profissionais-escala/buscar/', {
		token,
		params: {
			termo: termo || (vinculo ? undefined : cpf),
			cpf: vinculo ? cpf : undefined,
			estabelecimento: normalizeEstabelecimento(estabelecimento),
			vinculo,
			data_referencia: dataReferencia,
		},
	});
	if (result?.error) return result;
	return unwrapList(result);
};

export const requestSalvarProfissionalAmbienteMultiprofissional = ({ token, body } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/adicionar-profissional/', {
		token,
		body,
	});

export const requestAdicionarVacanciaAmbienteMultiprofissional = ({ token, body } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/adicionar-vacancia/', {
		token,
		body,
	});

export const requestPreverAcoesAmbienteMultiprofissional = ({ token, body } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/prever-acoes/', {
		token,
		body,
	});

export const requestAplicarAcoesAmbienteMultiprofissional = ({ token, body, previewEtag, idempotencyKey } = {}) =>
	request('post', 'escalas-mensais/ambiente-preenchimento/aplicar-acoes/', {
		token,
		body,
		headers: {
			...(previewEtag ? { 'If-Match': String(previewEtag).startsWith('"') ? String(previewEtag) : `"${previewEtag}"` } : {}),
			...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
		},
	});
